import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Intake, IntakeQuestion, IntakeQuestionOption } from "src/api/types";
import { useManageDrawer } from "src/hooks/useManageDrawer";
import UpdateIntakeDrawer from "./components/UpdateIntakeDrawer";
import UpdateQuestionDrawer from "./components/UpdateQuestionDrawer";
import UpdateOptionDrawer from "./components/UpdateOptionDrawer";

type ContextType = {
  updateActions: {
    handleOpen: () => void;
    handleClose: () => void;
  };
  intake: Intake | null;
  handleOpenUpdateIntake: (intake: Intake | null) => void;
  intakeQuestion: IntakeQuestion | null | undefined;
  handleOpenUpdateIntakeQuestion: (question: IntakeQuestion | null) => void;
  questionOption: IntakeQuestionOption | null;
  handleOpenUpdateIntakeQuestionOption: (
    option: IntakeQuestionOption | null,
  ) => void;
};

export const IntakesDrawerContext = createContext<ContextType>({
  updateActions: {
    handleClose: () => {},
    handleOpen: () => {},
  },
  intake: null,
  handleOpenUpdateIntake: () => {},
  intakeQuestion: null,
  handleOpenUpdateIntakeQuestion: () => {},
  questionOption: null,
  handleOpenUpdateIntakeQuestionOption: () => {},
});

interface Props {
  children: ReactNode;
}

const IntakesDrawerProvider = ({ children }: Props) => {
  const [openUpdate, updateActions] = useManageDrawer();
  const [intake, setIntake] = useState<Intake | null>(null);
  const [intakeQuestion, setIntakeQuestion] = useState<IntakeQuestion | null>();
  const [openUpdateQuestion, updateQuestionActions] = useManageDrawer();
  const [questionOption, setQuestionOption] =
    useState<IntakeQuestionOption | null>(null);
  const [openUpdateOption, updateOptionActions] = useManageDrawer();

  const handleOpenUpdateIntake = useCallback(
    (intake: Intake | null) => {
      setIntake(intake);
      updateActions.handleOpen();
    },
    [updateActions],
  );

  const handleOpenUpdateIntakeQuestion = useCallback(
    (question: IntakeQuestion | null) => {
      setIntakeQuestion(question);
      updateQuestionActions.handleOpen();
    },
    [updateQuestionActions],
  );

  const handleOpenUpdateIntakeQuestionOption = useCallback(
    (option: IntakeQuestionOption | null) => {
      setQuestionOption(option);
      updateOptionActions.handleOpen();
    },
    [updateOptionActions],
  );

  const value = useMemo(
    () => ({
      updateActions,
      intake,
      handleOpenUpdateIntake,
      intakeQuestion,
      handleOpenUpdateIntakeQuestion,
      questionOption,
      handleOpenUpdateIntakeQuestionOption,
    }),
    [
      handleOpenUpdateIntake,
      handleOpenUpdateIntakeQuestion,
      handleOpenUpdateIntakeQuestionOption,
      intake,
      intakeQuestion,
      questionOption,
      updateActions,
    ],
  );

  return (
    <IntakesDrawerContext.Provider value={value}>
      {children}
      {intake ? (
        <UpdateIntakeDrawer
          intake={intake}
          open={openUpdate}
          onClose={updateActions.handleClose}
        />
      ) : null}
      {intakeQuestion ? (
        <UpdateQuestionDrawer
          question={intakeQuestion}
          onClose={updateQuestionActions.handleClose}
          open={openUpdateQuestion}
        />
      ) : null}
      {questionOption ? (
        <UpdateOptionDrawer
          open={openUpdateOption}
          onClose={updateOptionActions.handleClose}
          option={questionOption}
        />
      ) : null}
    </IntakesDrawerContext.Provider>
  );
};

export default IntakesDrawerProvider;
